/* eslint-disable unicorn/no-null -- Capability tables expose `null` when providers omit optional features. */

import {
  AI_MODEL_PRICING,
  AUTO_MODEL_SENTINEL,
  canonicalizeModelId,
  DEFAULT_MODEL_ID,
  getModelDefinition,
  getModelOptions,
  type ModelCapability,
  type ModelProvider,
  type ModelTier,
} from '@/constants/ai';
import type { ModelOption } from '@/types/ai';

export const BASE_ANALYSIS_TWEET_LIMIT = 1000;
export const LARGE_CONTEXT_ANALYSIS_TWEET_LIMIT = 5000;
export const ULTRA_CONTEXT_ANALYSIS_TWEET_LIMIT = 10_000;

export type ModelContextTier = 'baseline' | 'standard' | 'large' | 'ultra';

interface ContextLimitRule {
  minTotalContextTokens: number;
  tier: Exclude<ModelContextTier, 'baseline'>;
  tweetLimit: number;
}

const CONTEXT_TWEET_LIMIT_RULES: readonly ContextLimitRule[] = [
  {
    tier: 'ultra',
    minTotalContextTokens: 2_000_000,
    tweetLimit: ULTRA_CONTEXT_ANALYSIS_TWEET_LIMIT,
  },
  {
    tier: 'large',
    minTotalContextTokens: 1_000_000,
    tweetLimit: LARGE_CONTEXT_ANALYSIS_TWEET_LIMIT,
  },
  {
    tier: 'standard',
    minTotalContextTokens: 200_000,
    tweetLimit: BASE_ANALYSIS_TWEET_LIMIT,
  },
] as const;

const BASELINE_CONTEXT: { tier: ModelContextTier; tweetLimit: number } = {
  tier: 'baseline',
  tweetLimit: BASE_ANALYSIS_TWEET_LIMIT,
};

const resolveContextTier = (totalContextTokens: number | null): { tier: ModelContextTier; tweetLimit: number } => {
  if (typeof totalContextTokens !== 'number' || Number.isNaN(totalContextTokens)) {
    return BASELINE_CONTEXT;
  }

  for (const rule of CONTEXT_TWEET_LIMIT_RULES) {
    if (totalContextTokens >= rule.minTotalContextTokens) {
      return { tier: rule.tier, tweetLimit: rule.tweetLimit };
    }
  }

  return BASELINE_CONTEXT;
};

const modelOptionsMap: Map<string, ModelOption> = new Map(getModelOptions().map((option) => [option.value, option]));

const EMPTY_CAPABILITIES: readonly ModelCapability[] = Object.freeze<ModelCapability[]>([]);

export interface ModelCapabilityProfile {
  canonicalId: string;
  capabilities: readonly ModelCapability[];
  contextTier: ModelContextTier;
  hasExtendedContext: boolean;
  originalId: string | null;
  provider: ModelProvider | null;
  tier: ModelTier | null;
  totalContextTokens: number | null;
  tweetAnalysisLimit: number;
}

export function resolveAnalysisModelId(candidate: string | null | undefined): string {
  if (!candidate) {
    return DEFAULT_MODEL_ID;
  }
  const trimmed = candidate.trim();
  if (trimmed.length === 0 || trimmed === AUTO_MODEL_SENTINEL) {
    return DEFAULT_MODEL_ID;
  }
  const canonical = canonicalizeModelId(trimmed);
  return canonical ?? trimmed;
}

function findPricingEntry(possibleIds: readonly string[]): (typeof AI_MODEL_PRICING)[string] | undefined {
  for (const id of possibleIds) {
    if (Object.hasOwn(AI_MODEL_PRICING, id)) {
      return AI_MODEL_PRICING[id];
    }
  }
  for (const id of possibleIds) {
    const canonical = canonicalizeModelId(id);
    if (!canonical) {
      continue;
    }
    const definition = getModelDefinition(canonical);
    const providerModelId = definition?.providerModelId;
    if (providerModelId && Object.hasOwn(AI_MODEL_PRICING, providerModelId)) {
      return AI_MODEL_PRICING[providerModelId];
    }
  }
  return;
}

export function getModelCapabilityProfile(modelId?: string | null): ModelCapabilityProfile {
  const resolvedId = resolveAnalysisModelId(modelId ?? null);
  const canonicalId = canonicalizeModelId(resolvedId) ?? resolvedId;

  const pricingEntry = findPricingEntry([canonicalId, canonicalId.toLowerCase(), resolvedId, resolvedId.toLowerCase()]);

  const totalContextTokens =
    typeof pricingEntry?.totalContextLength === 'number' ? pricingEntry.totalContextLength : null;
  const { tier: contextTier, tweetLimit: tweetAnalysisLimit } = resolveContextTier(totalContextTokens);
  const hasExtendedContext = tweetAnalysisLimit > BASE_ANALYSIS_TWEET_LIMIT;

  const option = modelOptionsMap.get(canonicalId) ?? null;

  return {
    canonicalId,
    originalId: modelId ?? null,
    provider: option?.provider ?? null,
    tier: option?.tier ?? null,
    capabilities: option?.capabilities ?? EMPTY_CAPABILITIES,
    totalContextTokens,
    hasExtendedContext,
    contextTier,
    tweetAnalysisLimit,
  };
}

export function getTweetAnalysisLimit(modelId?: string | null): number {
  return getModelCapabilityProfile(modelId).tweetAnalysisLimit;
}
